import { useMemo, useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrgQuery } from '@/lib/hooks/useOrgQuery'
import {
  Globe2,
  MoreHorizontal,
  Plus,
  Power,
  PowerOff,
  Search,
  ShieldCheck,
  ShieldOff,
} from 'lucide-react'
import { toast } from 'sonner'
import { apiFetch, apiJson, orgPath } from '@/lib/api/client'
import { formatDate } from '@/lib/utils'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import { EmptyState as SharedEmptyState } from '@/components/ui/empty-state'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { BigNumber } from '@/components/hawk-primitives'
import { useAuth } from '@/lib/auth/useAuth'

export interface Policy {
  id: string
  name: string
  description: string
  source: string
  scope: string | null
  enabled: boolean
  owner_id: string
  updated_by: string | null
  created_at: string
  updated_at: string
}

export const Route = createFileRoute('/_app/org/policies/')({
  component: PoliciesPage,
})

function PoliciesPage() {
  const navigate = useNavigate()
  const { currentOrgSlug } = useAuth()
  const [query, setQuery] = useState('')
  const [scopeFilter, setScopeFilter] = useState<string>('all')
  const [stateFilter, setStateFilter] = useState<string>('all')

  const policies = useOrgQuery<Policy[]>(['admin-policies'], '/admin/policies', {
    refetchInterval: 30_000,
    staleTime: 25_000,
  })

  const scopes = useMemo(() => {
    const all = policies.data ?? []
    const set = new Set<string>()
    for (const p of all) if (p.scope) set.add(p.scope)
    return Array.from(set).sort()
  }, [policies.data])

  const filtered = useMemo(() => {
    const all = policies.data ?? []
    return all.filter((p) => {
      if (stateFilter === 'enabled' && !p.enabled) return false
      if (stateFilter === 'disabled' && p.enabled) return false
      if (scopeFilter === 'global' && p.scope !== null) return false
      if (scopeFilter !== 'all' && scopeFilter !== 'global' && p.scope !== scopeFilter) return false
      if (!query) return true
      const q = query.toLowerCase()
      return (
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        (p.scope ?? '').toLowerCase().includes(q)
      )
    })
  }, [policies.data, query, scopeFilter, stateFilter])

  const kpis = useMemo(() => {
    const all = policies.data ?? []
    return {
      total: all.length,
      enabled: all.filter((p) => p.enabled).length,
      global: all.filter((p) => p.scope === null && p.enabled).length,
      scoped: all.filter((p) => p.scope !== null && p.enabled).length,
    }
  }, [policies.data])

  return (
    <div className="px-10 py-8">
      <PageHeader
        title="Policies"
        description="TypeScript lifecycle hooks evaluated for every request. A single deny short-circuits the chain; an unhandled error fails closed. Changes hot-reload across replicas via Postgres LISTEN/NOTIFY."
        right={
          <Button onClick={() => navigate({ to: '/org/policies/new' })}>
            <Plus className="size-4" />
            New policy
          </Button>
        }
      />

      <KpiBand kpis={kpis} loaded={!policies.isLoading} />

      <div className="flex items-center gap-3 mt-10 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground/70 pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, description, scope..."
            className="pl-9 bg-secondary border-transparent focus:border-primary/30 font-mono text-xs"
          />
        </div>

        <Select value={scopeFilter} onValueChange={setScopeFilter}>
          <SelectTrigger
            className="w-48 bg-secondary border-transparent"
            aria-label="Filter by scope"
          >
            <SelectValue placeholder="All scopes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All scopes</SelectItem>
            <SelectItem value="global">Global only</SelectItem>
            {scopes.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={stateFilter} onValueChange={setStateFilter}>
          <SelectTrigger
            className="w-40 bg-secondary border-transparent"
            aria-label="Filter by state"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All states</SelectItem>
            <SelectItem value="enabled">Enabled</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>

        <div className="ml-auto font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground tabular-nums">
          {filtered.length} of {(policies.data ?? []).length} shown
        </div>
      </div>

      {policies.isLoading ? (
        <div className="border border-border bg-card/30 rounded-md h-[320px] hawk-shimmer" />
      ) : filtered.length === 0 ? (
        <EmptyState hasAny={(policies.data ?? []).length > 0} />
      ) : (
        <div className="border border-border bg-card rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border bg-background/30">
                <TableHead className="w-12" />
                <TableHead className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Policy
                </TableHead>
                <TableHead className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Scope
                </TableHead>
                <TableHead className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  State
                </TableHead>
                <TableHead className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Updated
                </TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p, i) => (
                <PolicyRow
                  key={p.id}
                  policy={p}
                  delayMs={Math.min(i * 25, 200)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

function KpiBand({
  kpis,
  loaded,
}: {
  kpis: { total: number; enabled: number; global: number; scoped: number }
  loaded: boolean
}) {
  const cards = [
    { label: 'Policies', value: loaded ? kpis.total.toString() : '—', sub: 'defined', icon: ShieldCheck },
    { label: 'Enabled', value: loaded ? kpis.enabled.toString() : '—', sub: `of ${kpis.total}`, icon: Power, accent: true },
    { label: 'Global', value: loaded ? kpis.global.toString() : '—', sub: 'run for every repo', icon: Globe2 },
    { label: 'Scoped', value: loaded ? kpis.scoped.toString() : '—', sub: 'pinned to a repo', icon: ShieldCheck },
  ]
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 border border-border rounded-md bg-card/40 overflow-hidden">
      {cards.map((card, i) => {
        const Icon = card.icon
        return (
          <div
            key={card.label}
            className="relative px-6 py-6 animate-hawk-rise border-r border-b border-border last:border-r-0 lg:[&:nth-child(n+3)]:border-b-0 [&:nth-child(2n)]:border-r-0 lg:[&:nth-child(2n)]:border-r"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                {card.label}
              </span>
              <Icon
                className={
                  card.accent
                    ? 'size-3.5 text-primary'
                    : 'size-3.5 text-muted-foreground/50'
                }
                strokeWidth={1.5}
              />
            </div>
            <BigNumber value={card.value} className="text-[44px] mb-2" />
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              {card.sub}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function PolicyRow({ policy, delayMs }: { policy: Policy; delayMs: number }) {
  const queryClient = useQueryClient()
  const { currentOrgSlug } = useAuth()
  const [deleteOpen, setDeleteOpen] = useState(false)

  const toggle = useMutation({
    mutationFn: async () => {
      const r = await apiFetch(orgPath(`/admin/policies/${policy.id}`), {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !policy.enabled }),
      })
      if (!r.ok) throw new Error(await r.text())
    },
    onSuccess: () => {
      toast.success(policy.enabled ? `Disabled ${policy.name}` : `Enabled ${policy.name}`)
      queryClient.invalidateQueries({ queryKey: ['admin-policies', currentOrgSlug] })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const del = useMutation({
    mutationFn: async () => {
      const r = await apiFetch(orgPath(`/admin/policies/${policy.id}`), {
        method: 'DELETE',
      })
      if (!r.ok) throw new Error(await r.text())
    },
    onSuccess: () => {
      toast.success(`Deleted ${policy.name}`)
      queryClient.invalidateQueries({ queryKey: ['admin-policies', currentOrgSlug] })
      setDeleteOpen(false)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <TableRow
      className="group relative border-border hover:bg-popover animate-hawk-rise"
      style={{ animationDelay: `${delayMs}ms` }}
    >
      <TableCell className="w-12">
        <div
          className={`size-7 rounded-md flex items-center justify-center ring-1 ring-inset ${
            policy.enabled
              ? 'bg-primary/10 ring-primary/30 text-primary'
              : 'bg-secondary ring-border text-muted-foreground'
          }`}
        >
          {policy.enabled ? (
            <ShieldCheck className="size-3.5" strokeWidth={1.5} />
          ) : (
            <ShieldOff className="size-3.5" strokeWidth={1.5} />
          )}
        </div>
      </TableCell>
      <TableCell>
        <Link
          to="/org/policies/$id"
          params={{ id: policy.id }}
          className="text-sm font-medium hover:text-primary transition-colors"
        >
          {policy.name}
        </Link>
        {policy.description && (
          <div className="text-[11px] text-muted-foreground truncate max-w-md mt-0.5">
            {policy.description}
          </div>
        )}
      </TableCell>
      <TableCell>
        {policy.scope === null ? (
          <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-primary">
            <Globe2 className="size-3" strokeWidth={1.5} />
            Global
          </span>
        ) : (
          <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-secondary text-foreground/80 ring-1 ring-inset ring-border">
            {policy.scope}
          </span>
        )}
      </TableCell>
      <TableCell>
        <StateBadge enabled={policy.enabled} />
      </TableCell>
      <TableCell className="font-mono text-xs text-muted-foreground tabular-nums">
        {formatDate(policy.updated_at)}
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" className="text-muted-foreground">
              <MoreHorizontal className="size-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to="/org/policies/$id" params={{ id: policy.id }}>
                Edit
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={toggle.isPending}
              onClick={() => toggle.mutate()}
            >
              {policy.enabled ? (
                <>
                  <PowerOff className="size-3.5" />
                  Disable
                </>
              ) : (
                <>
                  <Power className="size-3.5" />
                  Enable
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete {policy.name}?</DialogTitle>
              <DialogDescription>
                The policy is removed from every replica within a second.
                Requests that depended on its allow/deny logic will pass through
                whichever policies remain.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={del.isPending}
                onClick={() => del.mutate()}
              >
                {del.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </TableCell>
    </TableRow>
  )
}

function StateBadge({ enabled }: { enabled: boolean }) {
  return (
    <span
      className={`inline-flex items-center font-mono text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded ring-1 ring-inset ${
        enabled
          ? 'bg-primary/10 text-primary ring-primary/30'
          : 'bg-secondary text-muted-foreground ring-border'
      }`}
    >
      {enabled ? 'Enabled' : 'Disabled'}
    </span>
  )
}

function EmptyState({ hasAny }: { hasAny: boolean }) {
  return (
    <SharedEmptyState
      eyebrow={hasAny ? 'No matches' : 'No policies yet'}
      title={
        hasAny ? 'Try a different filter.' : 'Every request currently passes through.'
      }
      description={
        hasAny
          ? 'Loosen the scope or state filter to see more policies.'
          : 'Write a TypeScript policy to deny vulnerable packages, block specific authors, or enforce supply-chain rules.'
      }
      action={
        !hasAny && (
          <Link
            to="/org/policies/new"
            className="inline-flex items-center gap-2 text-sm text-primary hover:underline underline-offset-4"
          >
            <Plus className="size-4" />
            Create your first policy
          </Link>
        )
      }
    />
  )
}
